/**
 * Default Junip (and similar) review widgets to "highest rating first" when the
 * widget exposes a native <select> sort control.
 *
 * On small viewports the sort <select> is often inside a closed filter sheet; we
 * click the filter/sort trigger once so controls mount, then apply the sort.
 *
 * Also hides Junip’s “Reset filters” control (not theme markup; matched by label).
 */
(function () {
  var MOBILE_MQ = '(max-width: 749px)';

  function isMobileViewport() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function looksLikeReviewSortSelect(select) {
    if (select.options.length < 2) return false;
    var labels = Array.from(select.options)
      .map(function (o) {
        return (o.textContent || '').toLowerCase();
      })
      .join(' | ');
    var values = Array.from(select.options)
      .map(function (o) {
        return (o.value || '').toLowerCase();
      })
      .join(' | ');
    var combined = labels + ' | ' + values;
    if (combined.includes('highest') || combined.includes('high_to_low') || combined.includes('high-to-low')) {
      return true;
    }
    var hasRatingWord = labels.includes('rating') || labels.includes('star');
    var hasTimeSort =
      labels.includes('recent') ||
      labels.includes('newest') ||
      labels.includes('oldest') ||
      labels.includes('latest') ||
      labels.includes('helpful');
    return hasRatingWord && hasTimeSort;
  }

  function findHighestRatingOption(select) {
    var options = Array.from(select.options);
    var byText = options.find(function (o) {
      var t = (o.textContent || '').toLowerCase();
      return (
        t.includes('highest') ||
        t.includes('high to low') ||
        (t.includes('rating') && (t.includes('high') || t.includes('top')))
      );
    });
    if (byText) return byText;
    return options.find(function (o) {
      var v = (o.value || '').toLowerCase();
      return (
        v.includes('highest') ||
        v.includes('rating_desc') ||
        v.includes('rating-desc') ||
        v.includes('high_to_low') ||
        v === 'rating_descending'
      );
    });
  }

  /**
   * @returns {{ sawSortSelect: boolean, changed: boolean }}
   */
  function applyWithinRoot(root) {
    var sawSortSelect = false;
    var changed = false;
    root.querySelectorAll('select').forEach(function (select) {
      if (!looksLikeReviewSortSelect(select)) return;
      sawSortSelect = true;
      var target = findHighestRatingOption(select);
      if (!target || select.value === target.value) return;
      select.value = target.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      changed = true;
    });
    return { sawSortSelect: sawSortSelect, changed: changed };
  }

  function junipWidgetScope(root) {
    return (
      root.querySelector('[class*="Junip"]') ||
      root.querySelector('[class*="junip"]') ||
      root.querySelector('[id*="Junip"]') ||
      root.querySelector('[id*="junip"]') ||
      root
    );
  }

  function hideResetFiltersButtons(root) {
    var scope = junipWidgetScope(root);
    scope.querySelectorAll('.junip-reset').forEach(function (el) {
      el.style.setProperty('display', 'none', 'important');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('tabindex', '-1');
    });
    scope.querySelectorAll('.junip-filters-text').forEach(function (wrap) {
      if (wrap.querySelector('.junip-reset') && wrap.children.length === 1) {
        wrap.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function tryOpenMobileFilterSheet(root) {
    if (!isMobileViewport()) return false;
    if (root.getAttribute('data-junip-filter-sheet-opened') === '1') return false;

    var scope = junipWidgetScope(root);
    var candidates = scope.querySelectorAll('button, [role="button"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.disabled || el.getAttribute('aria-disabled') === 'true') continue;

      var text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      var aria = (
        (el.getAttribute('aria-label') || '') +
        ' ' +
        (el.getAttribute('title') || '')
      ).toLowerCase();
      var label = text + ' ' + aria;

      if (label.includes('write') && label.includes('review')) continue;

      var looksFilter =
        /\bfilter(s)?\b/.test(label) ||
        /\bsort\b/.test(label) ||
        label.includes('refine') ||
        label.includes('show filters');

      if (!looksFilter) continue;

      if (label.includes('language') || label.includes('currency')) continue;

      el.click();
      root.setAttribute('data-junip-filter-sheet-opened', '1');
      return true;
    }
    return false;
  }

  function scheduleApplyPasses(root) {
    [80, 220, 500, 1000].forEach(function (ms) {
      setTimeout(function () {
        hideResetFiltersButtons(root);
        applyWithinRoot(root);
      }, ms);
    });
  }

  function watchRoot(root) {
    if (root.getAttribute('data-junip-sort-watching') === '1') return;
    root.setAttribute('data-junip-sort-watching', '1');
    var scheduled = null;

    function run() {
      if (scheduled) clearTimeout(scheduled);
      scheduled = setTimeout(function () {
        scheduled = null;

        hideResetFiltersButtons(root);

        var result = applyWithinRoot(root);

        if (isMobileViewport() && !result.sawSortSelect && root.getAttribute('data-junip-filter-sheet-opened') !== '1') {
          if (tryOpenMobileFilterSheet(root)) {
            scheduleApplyPasses(root);
            hideResetFiltersButtons(root);
            return;
          }
        }

        if (result.sawSortSelect && !result.changed) {
          return;
        }

        applyWithinRoot(root);
      }, 60);
    }

    run();
    var observer = new MutationObserver(run);
    observer.observe(root, { childList: true, subtree: true });

    window.addEventListener(
      'resize',
      function () {
        if (!isMobileViewport()) {
          root.removeAttribute('data-junip-filter-sheet-opened');
        }
      },
      { passive: true }
    );
  }

  function init() {
    document.querySelectorAll('[data-junip-default-sort-root]').forEach(watchRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
